import { create } from 'zustand';
import { api, setToken, setUnauthorizedHandler } from './api.js';
import { t } from './i18n.js';

/**
 * Sitzungszustand. Bewusst schlank: Nutzer, Organisation, Credit-Stand und
 * Kurzmeldungen. Alles andere lädt React Query direkt aus dem Backend.
 */

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
  orgId: string;
  /** true = meldet sich mit Google an und hat gar kein Passwort bei uns */
  google?: boolean;
  avatarUrl?: string;
  /**
   * Betreiber der Installation, nicht Eigentümer einer Organisation.
   * Jeder Kunde ist „owner" seiner eigenen Org — nur wer hier true hat,
   * sieht die betriebseigenen Ansichten.
   */
  operator?: boolean;
  /**
   * Konto ohne Guthaben (Freund, Testkonto). Sieht weder Credits noch
   * Tarife — für dieses Konto gibt es nichts zu kaufen.
   */
  unlimited?: boolean;
  /** Darf eigene Anbieter-Keys hinterlegen. Kunden dürfen das nicht. */
  ownKeys?: boolean;
  /**
   * Muss eigene Keys hinterlegen — es gibt für dieses Konto keinen Zugang
   * von uns. Ohne Key läuft nichts, nicht einmal die Planung.
   */
  ownKeysRequired?: boolean;
  /**
   * Die Kontostufe in einem Wort: operator (wir), friend (Testkonto mit
   * eigenen Keys), customer (zahlt Credits, nutzt unsere Zugänge).
   */
  tier?: 'operator' | 'friend' | 'customer';
}

export interface SessionOrg {
  id: string;
  name: string;
  plan: string;
  planName?: string;
  creditBalance: number;
  creditsReserved: number;
  available: number;
  limits?: { maxProjects: number; maxAccountsPerProject: number };
}

export interface Toast {
  id: number;
  text: string;
  tone: 'info' | 'ok' | 'err';
}

/**
 * Was die Paywall anzeigt.
 *
 * Eine Tarifgrenze ist keine Fehlermeldung, sondern ein Verkaufsmoment: Der
 * Nutzer soll im selben Augenblick sehen, WORAN es lag und WAS es kostet,
 * das aufzulösen. Deshalb liegt der Zustand global, jede Stelle, die auf
 * ein `plan_limit` läuft, kann ihn füllen.
 */
export interface PaywallState {
  open: boolean;
  /** Der Satz aus dem Backend, wörtlich, er nennt Tarif und Grenze */
  reason: string | null;
  limit: 'projects' | 'accounts' | 'agents' | 'credits' | null;
  /** Tarif, der das Problem löst, wird in der Liste hervorgehoben */
  suggestedPlan: string | null;
}

interface AuthState {
  user: SessionUser | null;
  org: SessionOrg | null;
  ready: boolean;
  toasts: Toast[];
  paywall: PaywallState;

  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (input: { email: string; password: string; name: string; orgName?: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshOrg: () => Promise<void>;
  setCredits: (balance: number, reserved?: number) => void;
  toast: (text: string, tone?: Toast['tone']) => void;
  dismissToast: (id: number) => void;
  openPaywall: (input?: Partial<Omit<PaywallState, 'open'>>) => void;
  closePaywall: () => void;
}

const CLOSED_PAYWALL: PaywallState = { open: false, reason: null, limit: null, suggestedPlan: null };

let toastSeq = 0;

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  org: null,
  ready: false,
  toasts: [],
  paywall: CLOSED_PAYWALL,

  async bootstrap() {
    // Refresh-Cookie einlösen; klappt das nicht, sind wir eben abgemeldet.
    const ok = await api.refresh();
    if (!ok) {
      set({ ready: true, user: null, org: null });
      return;
    }
    try {
      const me = await api.get<{ user: SessionUser; org: SessionOrg }>('/auth/me');
      set({ user: me.user, org: me.org, ready: true });
    } catch {
      set({ ready: true, user: null, org: null });
    }
  },

  async login(email, password) {
    const res = await api.post<{ token: string; user: SessionUser; org: SessionOrg }>(
      '/auth/login', { email, password },
    );
    setToken(res.token);
    const me = await api.get<{ user: SessionUser; org: SessionOrg }>('/auth/me');
    set({ user: me.user, org: me.org, ready: true });
  },

  async register(input) {
    await api.post('/auth/register', { ...input, acceptTerms: true });
    await api.post('/auth/logout').catch(() => {});
    setToken(null);
    set({ user: null, org: null, ready: true });
  },

  async logout() {
    await api.post('/auth/logout').catch(() => {});
    setToken(null);
    set({ user: null, org: null });
  },

  async refreshOrg() {
    try {
      const me = await api.get<{ user: SessionUser; org: SessionOrg }>('/auth/me');
      set({ user: me.user, org: me.org });
    } catch { /* nicht kritisch */ }
  },

  setCredits(balance, reserved) {
    const org = get().org;
    if (!org) return;
    const held = reserved ?? org.creditsReserved;
    set({ org: { ...org, creditBalance: balance, creditsReserved: held, available: Math.max(0, balance - held) } });
  },

  toast(text, tone = 'info') {
    const id = ++toastSeq;
    set((s) => ({ toasts: [...s.toasts, { id, text, tone }] }));
    setTimeout(() => get().dismissToast(id), 4200);
  },

  dismissToast(id) {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },

  openPaywall(input = {}) {
    /*
     * Konten ohne Guthaben sehen nie eine Paywall.
     *
     * Fuer sie gibt es nichts zu kaufen: Sie laufen ueber eigene Keys und
     * kennen keine Tarifgrenze. Ein Kauf-Dialog waere dort nicht nur
     * ueberfluessig, sondern verwirrend -- er verspricht eine Loesung fuer
     * ein Problem, das dieses Konto gar nicht hat.
     */
    if (get().user?.unlimited) return;
    set({ paywall: { ...CLOSED_PAYWALL, ...input, open: true } });
  },

  closePaywall() {
    set({ paywall: CLOSED_PAYWALL });
  },
}));

/**
 * Fehler aus dem Backend einheitlich behandeln.
 *
 * Läuft ein Aufruf gegen eine Tarifgrenze oder gegen leeres Guthaben, öffnet
 * sich die Paywall, mit dem Originaltext obendrauf, damit sofort klar ist,
 * warum sie da ist. Alles andere landet als Kurzmeldung.
 *
 * Gibt true zurück, wenn die Paywall den Fall übernommen hat.
 */
export function reportApiError(err: unknown, fallback = t('Das hat nicht geklappt.')): boolean {
  const e = err as { code?: string; status?: number; message?: string; details?: Record<string, unknown> };
  const store = useAuth.getState();

  if (e?.code === 'plan_limit') {
    const details = e.details ?? {};
    store.openPaywall({
      reason: e.message ?? fallback,
      limit: (details.limit as PaywallState['limit']) ?? 'projects',
      suggestedPlan: (details.suggestedPlan as string | null) ?? null,
    });
    return true;
  }

  if (e?.status === 402 || e?.code === 'insufficient_credits') {
    store.openPaywall({ reason: e.message ?? fallback, limit: 'credits', suggestedPlan: null });
    return true;
  }

  store.toast(e?.message ?? fallback, 'err');
  return false;
}

// Ein abgelaufener Token wirft den Nutzer sauber auf die Anmeldung zurück,
// statt die Oberfläche in einen halb geladenen Zustand kippen zu lassen.
setUnauthorizedHandler(() => {
  setToken(null);
  useAuth.setState({ user: null, org: null, ready: true });
});

/* ─────────────────────────────────────────────── Oberfläche (Theme, Tipps) */

export type Theme = 'dark' | 'light';
export type Accent = 'ultramarin' | 'tangerine' | 'smaragd';

function lies(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function schreib(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* privater Modus */ }
}

function systemTheme(): Theme {
  try {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

/** Setzt Theme und Akzent auf <html> und passt die Browserleiste an. */
export function applyTheme(theme: Theme, accent: Accent): void {
  const root = document.documentElement;
  root.dataset.theme = theme;
  if (accent === 'ultramarin') delete root.dataset.accent;
  else root.dataset.accent = accent;
  document.querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'light' ? '#f6f7f9' : '#1b1c20');
}

interface UiState {
  theme: Theme;
  /** true = der Nutzer hat selbst gewählt, sonst folgt es dem System */
  themeChosen: boolean;
  accent: Accent;
  tipsOn: boolean;
  dockOpen: boolean;
  setTheme: (theme: Theme) => void;
  /** Gespeicherte Wahl verwerfen und wieder der Systemeinstellung folgen */
  followSystem: () => void;
  toggleTheme: () => void;
  setAccent: (accent: Accent) => void;
  setTips: (on: boolean) => void;
  setDock: (open: boolean) => void;
}

const gespeichertesTheme = lies('aff-theme');

/**
 * Was nur die Oberfläche betrifft und im Browser bleibt: Hell/Dunkel,
 * Akzent, ob die Tipps „So funktioniert's" zu sehen sind und ob die
 * Aktivitätsleiste offen ist. Ohne gespeicherte Wahl folgt das Theme der
 * Systemeinstellung -- auch wenn die sich später ändert.
 */
export const useUi = create<UiState>((set, get) => ({
  theme: gespeichertesTheme === 'light' || gespeichertesTheme === 'dark' ? gespeichertesTheme : systemTheme(),
  themeChosen: gespeichertesTheme === 'light' || gespeichertesTheme === 'dark',
  accent: (['tangerine', 'smaragd'].includes(lies('aff-accent') ?? '') ? lies('aff-accent') : 'ultramarin') as Accent,
  tipsOn: lies('aff-tips') !== 'off',
  dockOpen: lies('aff-dock') === 'open',

  setTheme(theme) {
    schreib('aff-theme', theme);
    set({ theme, themeChosen: true });
    applyTheme(theme, get().accent);
  },
  followSystem() {
    try { localStorage.removeItem('aff-theme'); } catch { /* privater Modus */ }
    const theme = systemTheme();
    set({ theme, themeChosen: false });
    applyTheme(theme, get().accent);
  },
  toggleTheme() {
    get().setTheme(get().theme === 'dark' ? 'light' : 'dark');
  },
  setAccent(accent) {
    schreib('aff-accent', accent);
    set({ accent });
    applyTheme(get().theme, accent);
  },
  setTips(on) {
    schreib('aff-tips', on ? 'on' : 'off');
    set({ tipsOn: on });
  },
  setDock(open) {
    schreib('aff-dock', open ? 'open' : 'closed');
    set({ dockOpen: open });
  },
}));

applyTheme(useUi.getState().theme, useUi.getState().accent);

try {
  window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
    const s = useUi.getState();
    if (s.themeChosen) return;
    const theme = systemTheme();
    useUi.setState({ theme });
    applyTheme(theme, s.accent);
  });
} catch { /* alte Browser */ }
